import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LegalConsentService } from '../../../core/legal/legal-consent.service';
import { AuthService } from '../../../core/auth/auth.service';
import { Role } from '../../../models/enums';
import { LegalDocumentType, LegalDocumentVersion } from '../../../models/interfaces';
import { ThemeToggleComponent } from '../../ui/buttons/theme-toggle/theme-toggle.component';
import { LegalMarkdownService } from './legal-markdown.service';

/**
 * Libellé humain affiché en titre pour chaque type de document légal.
 * Exporté pour être réutilisé par `app-legal-consent-gate` (P4) — un seul
 * mapping type → libellé à maintenir, plutôt qu'une copie dupliquée.
 */
export const DOCUMENT_LABELS: Record<LegalDocumentType, string> = {
  terms: 'Conditions Générales d\'Utilisation',
  privacy: 'Politique de confidentialité',
  legal_notices: 'Mentions légales',
  cgv: 'Conditions Générales de Vente',
  dpa: 'Accord de sous-traitance des données (DPA)'
};

/**
 * Slug d'URL public de chaque document légal, servant la route unique
 * `/documents/:slug` (MOKA-212, remplace les anciennes routes dédiées
 * `/terms`, `/privacy-policy`, `/legal-notices`). Les 3 slugs historiques
 * reprennent à l'identique les anciennes URLs pour ne pas casser les liens
 * déjà diffusés.
 */
export const LEGAL_DOCUMENT_SLUGS: Record<LegalDocumentType, string> = {
  terms: 'terms',
  privacy: 'privacy-policy',
  legal_notices: 'legal-notices',
  cgv: 'cgv',
  dpa: 'dpa'
};

/**
 * Résolution inverse slug → type, utilisée par `app-legal-document-by-slug`
 * (route `/documents/:slug`). Retourne `null` si aucun type ne correspond
 * (le composant appelant redirige alors vers `/404`).
 */
export function legalDocumentTypeFromSlug(slug: string): LegalDocumentType | null {
  const entry = (Object.entries(LEGAL_DOCUMENT_SLUGS) as [LegalDocumentType, string][])
    .find(([, documentSlug]) => documentSlug === slug);
  return entry ? entry[0] : null;
}

/**
 * Route publique + libellé de chacun des documents, pour les liens de pied
 * de page (et, en P4, le lien « ouvrir dans une page » du gate). `path`
 * dérive de `LEGAL_DOCUMENT_SLUGS` pour ne pas dupliquer le mapping.
 */
export const DOCUMENT_LINKS: Record<LegalDocumentType, { path: string; label: string }> = {
  terms: { path: `/documents/${LEGAL_DOCUMENT_SLUGS.terms}`, label: 'CGU' },
  privacy: { path: `/documents/${LEGAL_DOCUMENT_SLUGS.privacy}`, label: 'Politique de confidentialité' },
  legal_notices: { path: `/documents/${LEGAL_DOCUMENT_SLUGS.legal_notices}`, label: 'Mentions légales' },
  cgv: { path: `/documents/${LEGAL_DOCUMENT_SLUGS.cgv}`, label: 'CGV' },
  dpa: { path: `/documents/${LEGAL_DOCUMENT_SLUGS.dpa}`, label: 'DPA' }
};

/**
 * Page publique générique pour l'affichage d'un document légal (CGU, politique de
 * confidentialité, mentions légales) : titre, version/date en vigueur, contenu markdown rendu, et
 * navigation dans l'historique des versions publiées.
 *
 * Point d'attention pour une itération future (hors scope P3) : l'ancien sommaire sticky à ancres
 * codées en dur (`#section-N`) a été retiré — ces ancres n'existent pas dans du HTML markdown
 * généré à la volée. Un sommaire pourra être réintroduit plus tard en générant les ancres depuis
 * les `<h2>` du contenu rendu.
 */
@Component({
  selector: 'app-legal-document-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, DatePipe, ThemeToggleComponent],
  templateUrl: './legal-document-page.component.html',
  styleUrl: './legal-document-page.component.scss'
})
export class LegalDocumentPageComponent {
  private auth = inject(AuthService);
  private legalConsentService = inject(LegalConsentService);
  private legalMarkdownService = inject(LegalMarkdownService);
  private route = inject(ActivatedRoute);


  /**
   * `true` quand la page est affichée DANS l'application (utilisateur
   * connecté), `false` sur l'écran public.
   *
   * Ces pages ont deux vies : document autonome accessible avant connexion, et
   * page consultable depuis l'application. Le même rendu pour les deux donnait
   * une page entière imbriquée dans le layout — double fond, carte blanche à
   * bords carrés, logo Mokah+ en double avec la sidebar, sélecteur de thème en
   * double avec le header, et un « Retour à la connexion » proposé à quelqu'un
   * déjà connecté. Même critère que `app.component.html` pour afficher le
   * layout, donc les deux ne peuvent pas diverger.
   */
  protected isEmbedded = computed(() => {
    this.auth.profileRevision();
    return !!this.auth.profile;
  });
  type = input.required<LegalDocumentType>();

  readonly currentYear = new Date().getFullYear();

  title = computed(() => DOCUMENT_LABELS[this.type()]);

  siblingLinks = computed(() =>
    (Object.keys(DOCUMENT_LINKS) as LegalDocumentType[])
      .filter(key => key !== this.type())
      .map(key => DOCUMENT_LINKS[key])
  );

  currentVersion = computed(() => this.legalConsentService.currentVersions()[this.type()]);

  #archivedVersion = signal<LegalDocumentVersion | null>(null);
  archivedVersion = computed(() => this.#archivedVersion());
  isArchivedView = computed(() => this.archivedVersion() !== null);

  displayedVersion = computed<LegalDocumentVersion | null>(() =>
    this.archivedVersion() ?? this.currentVersion() ?? null
  );

  renderedContent = computed(() => {
    const version = this.displayedVersion();
    return version ? this.legalMarkdownService.renderToSafeHtml(version.content) : '';
  });

  effectiveDate = computed(() => {
    const version = this.displayedVersion();
    return version ? version.effective_at ?? version.published_at : null;
  });

  // ── Signature au nom de l'organisation (P4) ─────────────────────────────
  //
  // Uniquement pertinent pour un document `scope === 'organisation'` (CGV,
  // DPA) : les 3 documents historiques (`terms`/`privacy`/`legal_notices`)
  // sont `scope === 'user'`, donc `isOrganisationDocument()` y est toujours
  // `false` et rien ci-dessous ne s'affiche pour eux.

  /** Portée du document affiché — `null` tant que `allDocuments()` n'est pas chargé (bootstrap/hub). */
  documentScope = computed(() => this.legalConsentService.allDocuments().find(d => d.type === this.type())?.scope ?? null);

  isOrganisationDocument = computed(() => this.documentScope() === 'organisation');

  /** Lit `profileRevision()` — même piège que `isEmbedded` ci-dessus (getter non réactif). */
  isOrganisationAdmin = computed(() => {
    this.auth.profileRevision();
    return this.auth.hasRole([Role.SuperAdmin]);
  });

  /** `true` si l'organisation active a déjà un consentement pour la version EN VIGUEUR affichée. */
  organisationAlreadySigned = computed(() => {
    const version = this.currentVersion();
    if (!version) return false;
    return this.legalConsentService.organisationConsents().some(consent => consent.version_id === version.id);
  });

  /** Consentement d'organisation correspondant, pour afficher qui a signé et quand une fois signé. */
  organisationConsent = computed(() => {
    const version = this.currentVersion();
    if (!version) return null;
    return this.legalConsentService.organisationConsents().find(consent => consent.version_id === version.id) ?? null;
  });

  /** Nom de l'organisation active, pour la confirmation de signature — `auth` est privé, le template ne peut pas y accéder directement. */
  organisationLabel = computed(() => {
    this.auth.profileRevision();
    return this.auth.profile?.organisation?.label ?? null;
  });

  /**
   * Action de signature visible ssi : document de portée organisation, admin
   * connecté, une version en vigueur existe, elle n'est pas déjà signée par
   * l'organisation, et on n'est pas en train de consulter une version
   * archivée (on ne signe jamais autre chose que la version en vigueur).
   *
   * Visible que la version exige ou non une re-signature (`requires_resignature`)
   * — un document jamais signé (état « neutre » du hub, P2) doit pouvoir être
   * signé au même titre qu'un document exigeant explicitement une re-signature ;
   * seul le TON du bloc change entre les deux, cf. `signatureIsRequired` et le
   * template.
   */
  showOrganisationSignAction = computed(() =>
    this.isOrganisationDocument()
    && this.isOrganisationAdmin()
    && !!this.currentVersion()
    && !this.isArchivedView()
    && !this.organisationAlreadySigned()
  );

  /**
   * `true` ssi la version en vigueur exige explicitement une re-signature —
   * seul ce cas justifie une formulation « Signature requise » (urgence
   * réelle, cf. `organisationPendingConsent` côté service). Un document
   * jamais signé mais qui n'exige rien (état « neutre » du hub, P2) reste
   * proposable ci-dessous mais avec un ton neutre, pas alarmiste.
   */
  signatureIsRequired = computed(() => this.currentVersion()?.requires_resignature ?? false);

  /** Coché avant que le bouton de signature ne devienne actif — même exigence que le gate CGV de la souscription (Phase 3). */
  #signAcknowledged = signal(false);
  signAcknowledged = computed(() => this.#signAcknowledged());

  toggleSignAcknowledged(): void {
    this.#signAcknowledged.update(checked => !checked);
  }

  /**
   * Libellé de la case à cocher, nommant explicitement l'organisation quand
   * son libellé est disponible — même traitement que `cgvCheckboxLabel` du
   * dialog de confirmation d'abonnement (Phase 3), pour ne jamais laisser un
   * admin engager sa personne morale sur un simple clic sans avoir affirmé
   * explicitement au nom de qui il signe.
   */
  signCheckboxLabel = computed(() => {
    const label = this.organisationLabel();
    const documentTitle = this.title();
    return label
      ? `J'ai lu et j'accepte ${documentTitle} au nom de ${label}`
      : `J'ai lu et j'accepte ${documentTitle} au nom de mon organisation`;
  });

  /** Même repli que `signCheckboxLabel`, pour le libellé du bouton d'action. */
  signButtonLabel = computed(() => {
    const label = this.organisationLabel();
    return label ? `Signer au nom de ${label}` : 'Signer au nom de mon organisation';
  });

  #signing = signal(false);
  signing = computed(() => this.#signing());

  #signError = signal<string | null>(null);
  signError = computed(() => this.#signError());

  /**
   * Signe la version en vigueur au nom de l'organisation active. Le bouton
   * n'est actionnable que si `showOrganisationSignAction()` ET
   * `signAcknowledged()` sont vrais (case cochée), donc
   * `organisationId`/`version` manquants ou case décochée ne devrait jamais
   * arriver en pratique — garde-fou défensif, pas un cas attendu.
   *
   * Aucun toggle manuel de succès nécessaire : `organisationAlreadySigned()`
   * redevient vrai automatiquement dès que `consent()` met à jour
   * `organisationConsents()` côté service (le bloc entier disparaît alors du
   * template, la case cochée avec).
   */
  async signOnBehalfOfOrganisation(): Promise<void> {
    const organisationId = this.auth.getOrganisationId();
    const version = this.currentVersion();
    if (organisationId === null || !version || !this.#signAcknowledged()) {
      console.error('LegalDocumentPageComponent.signOnBehalfOfOrganisation: missing organisationId/currentVersion or acknowledgement not given');
      this.#signError.set('Impossible de signer ce document pour le moment. Merci de recharger la page.');
      return;
    }

    this.#signing.set(true);
    this.#signError.set(null);
    try {
      const { error } = await this.legalConsentService.consent(version.id, 'gate', organisationId);
      if (error) throw error;
    } catch (error) {
      console.error('LegalDocumentPageComponent.signOnBehalfOfOrganisation failed', error);
      this.#signError.set('Une erreur est survenue lors de l\'enregistrement de la signature. Merci de réessayer.');
    } finally {
      this.#signing.set(false);
    }
  }

  /**
   * Historique des versions. Plus aucun sélecteur ne l'expose (retiré le
   * 23/09/2026 : un lecteur n'a pas à choisir sa version), mais il reste chargé
   * à la demande quand une version archivée est affichée — c'est lui qui permet
   * de dire PAR QUOI elle a été remplacée.
   */
  #history = signal<LegalDocumentVersion[] | null>(null);


  #versionSwitchError = signal(false);
  versionSwitchError = computed(() => this.#versionSwitchError());

  /** Version suivante connue (depuis l'historique déjà chargé) qui a remplacé la version archivée affichée. */
  replacedByVersion = computed<LegalDocumentVersion | null>(() => {
    const archived = this.archivedVersion();
    const history = this.#history();
    if (!archived || !history) return null;
    const nextVersions = history.filter(version => version.version > archived.version);
    return nextVersions.sort((a, b) => a.version - b.version)[0] ?? null;
  });

  #retryAttempted = signal(false);
  #retryInFlight = signal(false);

  /**
   * Lien profond depuis « Mes consentements » (P7) : `?version=<id>` ouvre
   * directement la version archivée signée, sans repasser par le panneau
   * d'historique. Attend que `currentVersions` soit chargé pour ce type
   * (bootstrap ou retry ci-dessous) avant de comparer, pour ne pas afficher
   * à tort le bandeau « version archivée » sur la version en vigueur.
   */
  #queryVersionApplied = signal(false);

  /**
   * Panneau de secours affiché uniquement quand, après une tentative de rechargement direct
   * (`loadCurrentVersions`), aucune version en vigueur n'est disponible pour ce type de document
   * — jamais de page blanche (échec réseau, ou aucune version publiée).
   */
  showFallback = computed(() =>
    !this.displayedVersion() && this.#retryAttempted() && !this.#retryInFlight()
  );

  constructor() {
    // Le signal currentVersions est déjà préchargé au bootstrap de session (fail-open). S'il est
    // toujours vide à l'affichage de cette page (page publique, session ou non), on retente un
    // appel direct — une seule fois — avant de basculer sur le fallback.
    effect(() => {
      const hasCurrentVersion = !!this.legalConsentService.currentVersions()[this.type()];
      if (!hasCurrentVersion && !this.#retryAttempted() && !this.#retryInFlight()) {
        void this.retryLoadCurrentVersion();
      }
    });

    effect(() => {
      if (this.#queryVersionApplied()) return;
      const requestedVersionId = this.route.snapshot.queryParamMap.get('version');
      if (!requestedVersionId) {
        this.#queryVersionApplied.set(true);
        return;
      }
      if (!this.legalConsentService.currentVersions()[this.type()]) return;

      this.#queryVersionApplied.set(true);
      if (requestedVersionId !== this.currentVersion()?.id) {
        void this.viewVersion(requestedVersionId);
      }
    });
  }

  private async retryLoadCurrentVersion(): Promise<void> {
    this.#retryInFlight.set(true);
    try {
      await this.legalConsentService.loadCurrentVersions();
    } finally {
      this.#retryInFlight.set(false);
      this.#retryAttempted.set(true);
    }
  }

  /**
   * Charge l'historique une seule fois, pour alimenter `replacedByVersion`.
   * Silencieux : un échec prive le bandeau d'archive de la phrase « remplacée
   * par la version N », il ne doit pas empêcher la lecture du document.
   */
  async #ensureHistoryLoaded(): Promise<void> {
    if (this.#history() !== null) return;
    const { data, error } = await this.legalConsentService.getVersionHistory(this.type());
    if (!error && data) this.#history.set(data);
  }

  async viewVersion(versionId: string): Promise<void> {
    this.#versionSwitchError.set(false);
    const { data, error } = await this.legalConsentService.getVersion(versionId);
    if (error || !data) {
      this.#versionSwitchError.set(true);
      return;
    }
    this.#archivedVersion.set(data);
    // Une version archivée n'a d'intérêt que si l'on sait ce qui l'a remplacée.
    void this.#ensureHistoryLoaded();
  }

  returnToCurrentVersion(): void {
    this.#archivedVersion.set(null);
    this.#versionSwitchError.set(false);
  }
}
